
import React, { useState } from 'react';
import { UserProfile } from '../types';
import { deriveKeyFromPassword, unwrapKey, wrapKey, generateSalt, RECOVERY_SALT } from '../utils/crypto';

interface VaultRecoveryProps {
  user: UserProfile;
  onComplete: (key: string, salt: string, wrappedRecovery: string, wrappedPassword: string) => Promise<void>;
  onClose: () => void;
}

const VaultRecovery: React.FC<VaultRecoveryProps> = ({ user, onComplete, onClose }) => {
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveredMasterDataKey, setRecoveredMasterDataKey] = useState<CryptoKey | null>(null);

  const handleVerify = async () => {
    setError('');
    setLoading(true);
    try {
      // 1. Deriva a chave de acesso a partir da string de 24 caracteres
      const recoveryAccessKey = await deriveKeyFromPassword(recoveryKey, RECOVERY_SALT);
      
      // 2. Tenta "Desempacotar" a MasterDataKey real (Coluna J - wrapped_key)
      const masterDataKey = await unwrapKey(user.wrapped_key!, recoveryAccessKey);
      
      if (masterDataKey) {
        setRecoveredMasterDataKey(masterDataKey);
        setStep(2);
      } else {
        setError("Chave de recuperação inválida.");
      }
    } catch {
      setError("Erro ao verificar chave.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (newPassword.length < 8) {
      setError("A nova senha deve ter 8 caracteres.");
      return;
    }
    if (!recoveredMasterDataKey) return;

    setLoading(true);
    try {
      // 1. Novo Salt para a nova senha
      const newSalt = generateSalt();
      
      // 2. Novas chaves de acesso
      const newPasswordAccessKey = await deriveKeyFromPassword(newPassword, newSalt);
      const recoveryAccessKey = await deriveKeyFromPassword(recoveryKey, RECOVERY_SALT);
      
      // 3. Re-protege a MESMA MasterDataKey com os novos segredos
      const wrappedByRecovery = await wrapKey(recoveredMasterDataKey, recoveryAccessKey);
      const wrappedByPassword = await wrapKey(recoveredMasterDataKey, newPasswordAccessKey);
      
      // 4. Salva as novas proteções, mantendo a chave de dados original (dados salvos continuam legíveis)
      await onComplete(newPassword, newSalt, wrappedByRecovery, wrappedByPassword);
    } catch {
      setError("Erro ao redefinir acesso.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-slate-950/90 backdrop-blur-xl flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl border border-blue-500/20">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600/10 text-blue-500 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 border border-blue-500/20">
            <i className="fa-solid fa-life-ring"></i>
          </div>
          <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Recuperação</h3>
        </div>

        {step === 1 ? (
          <div className="space-y-6">
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest text-center">Insira sua Chave de Backup</p>
            <input value={recoveryKey} onChange={e => setRecoveryKey(e.target.value.toUpperCase())} className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl p-5 text-center font-mono text-sm font-black border-none outline-none focus:ring-2 focus:ring-blue-500" placeholder="XXXXXX-XXXXXX-..." />
            {error && <p className="text-red-500 text-[9px] font-black text-center uppercase">{error}</p>}
            <button onClick={handleVerify} disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg">
              {loading ? "Verificando..." : "Verificar Chave"}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-[10px] text-emerald-500 font-black uppercase tracking-widest text-center">Chave Confirmada! Defina nova senha:</p>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-800 rounded-2xl p-5 text-center text-sm font-black border-none outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nova Senha Mestra" />
            <button onClick={handleReset} disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg">
              {loading ? "Salvando..." : "Redefinir Acesso"}
            </button>
          </div>
        )}
        <button onClick={onClose} className="w-full mt-4 text-slate-400 text-[9px] font-black uppercase tracking-widest">Cancelar</button>
      </div>
    </div>
  );
};

export default VaultRecovery;
